import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

export const HOVER_DELAY_MS = 400

// A tooltip in the workspace's own style, replacing the browser's title
// tooltip (slow to appear, unstyled, and never shown on touch). Hover shows
// it after a short delay, so passing over doesn't flash it; keyboard focus
// and a tap show it at once; leaving, Escape, or a tap elsewhere closes it.
// The trigger is a focusable span with role="button", not a <button>: it
// often sits inside a field's <label>, and a real button there becomes a
// second labelled control for that label. Enter or Space toggles it, and the
// text is wired as its description for screen readers.
export const Tooltip = ({ text, className = '', children }) => {
  const [open, setOpen] = useState(false)
  const timer = useRef(null)
  const root = useRef(null)
  const id = useId()

  const clear = useCallback(() => { if (timer.current) { clearTimeout(timer.current); timer.current = null } }, [])
  const show = useCallback(() => { clear(); setOpen(true) }, [clear])
  const hide = useCallback(() => { clear(); setOpen(false) }, [clear])

  useEffect(() => clear, [clear])

  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') hide() }
    const onDown = e => { if (root.current && !root.current.contains(e.target)) hide() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown) }
  }, [open, hide])

  return (
    <span className="sw-tip-wrap" ref={root}>
      <span role="button" tabIndex={0} className={`sw-tip-trigger ${className}`} aria-describedby={id} aria-expanded={open}
        onPointerEnter={e => { if (e.pointerType !== 'touch') { clear(); timer.current = setTimeout(show, HOVER_DELAY_MS) } }}
        onPointerLeave={e => { if (e.pointerType !== 'touch') hide() }}
        onFocus={show} onBlur={hide}
        onClick={e => { e.preventDefault(); e.stopPropagation(); open ? hide() : show() }}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open ? hide() : show() } }}>
        {children}
      </span>
      <span role="tooltip" id={id} className={`sw-tip${open ? ' sw-tip-open' : ''}`}>{text}</span>
    </span>
  )
}
